import React, { useEffect, useState } from "react";
import { subscription, checkout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { X, Check, Sparkles, ArrowRight, Gift } from "lucide-react";
import GiftModal from "./GiftModal";

export default function PaywallModal({ open, onClose, reason }) {
  const { user } = useAuth();
  const [packages, setPackages] = useState(null);
  const [busy, setBusy] = useState(null); // holds the tier id being purchased
  const [giftOpen, setGiftOpen] = useState(false);

  useEffect(() => {
    if (open && !packages) {
      subscription.packages().then((d) => setPackages(d.packages)).catch(() => {});
    }
  }, [open, packages]);

  if (!open) return null;

  const pro = packages?.pro;
  const proAnnual = packages?.pro_annual;
  const monthlyAmount = pro?.amount ?? 3.99;
  const annualAmount = proAnnual?.amount ?? 39.99;

  const startCheckout = async (tier) => {
    setBusy(tier);
    try {
      const { url } = await checkout.create(tier, window.location.origin);
      if (!url) {
        toast.error("Could not start checkout");
        setBusy(null);
        return;
      }
      // Escape iframe (preview environments) — Stripe Checkout can't load in iframes.
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch (_) {
        /* cross-origin iframe */
      }
      const newTab = window.open(url, "_blank", "noopener,noreferrer");
      if (newTab) {
        setBusy(null);
      } else {
        window.location.href = url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-[100] bg-[#2E2E2E]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 fade-in-up"
      onClick={onClose}
    >
      <div
        className="bg-[#F9FAF7] rounded-t-3xl sm:rounded-3xl w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-[0_-8px_40px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Compact header */}
        <div className="sticky top-0 bg-[#F9FAF7]/95 backdrop-blur-sm flex items-start justify-between px-5 py-3.5 border-b border-[#DCE5DB]">
          <div className="min-w-0 pr-2">
            <h2 className="font-display text-[19px] sm:text-xl text-[#2E2E2E] leading-tight">
              Your LifeGuide journey is saved
            </h2>
            <p className="text-xs text-[#4E5A4C] mt-0.5 leading-snug">
              {reason ||
                "Your conversations, journal, and saved wisdom are still here. Continue with Pro for unlimited access."}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="paywall-close"
            className="text-[#6B6B6B] hover:text-[#2E2E2E] p-1.5 rounded-full hover:bg-[#EEF3EC] transition shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-5 sm:px-6 sm:pt-5 sm:pb-6">
          {/* Free vs Pro comparison — the reason to upgrade */}
          <div
            data-testid="paywall-compare"
            className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-4"
          >
            <div className="rounded-2xl border border-[#DCE5DB] bg-white p-3.5 sm:p-4">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#6B6B6B] font-semibold mb-1.5">
                Free
              </div>
              <ul className="space-y-1 text-[12.5px] sm:text-[13px] text-[#3B3B3B] leading-snug">
                <li className="flex gap-1.5">
                  <span className="text-[#6B6B6B]">·</span>1 Coach reply / day
                </li>
                <li className="flex gap-1.5">
                  <span className="text-[#6B6B6B]">·</span>1 Guidance Moment / day
                </li>
                <li className="flex gap-1.5">
                  <span className="text-[#6B6B6B]">·</span>Basic access — forever
                </li>
              </ul>
            </div>
            <div className="rounded-2xl border border-[#4F6F52]/30 bg-[#EEF3EC] p-3.5 sm:p-4">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1.5 flex items-center gap-1">
                <Sparkles size={10} strokeWidth={2.4} /> Pro
              </div>
              <ul className="space-y-1 text-[12.5px] sm:text-[13px] text-[#2E2E2E] leading-snug">
                <li className="flex gap-1.5">
                  <Check size={12} className="mt-0.5 text-[#4F6F52] shrink-0" strokeWidth={2.4} />
                  Unlimited Coach
                </li>
                <li className="flex gap-1.5">
                  <Check size={12} className="mt-0.5 text-[#4F6F52] shrink-0" strokeWidth={2.4} />
                  Unlimited Guidance
                </li>
                <li className="flex gap-1.5">
                  <Check size={12} className="mt-0.5 text-[#4F6F52] shrink-0" strokeWidth={2.4} />
                  Private Journal
                </li>
                <li className="flex gap-1.5">
                  <Check size={12} className="mt-0.5 text-[#4F6F52] shrink-0" strokeWidth={2.4} />
                  Saved Wisdom
                </li>
                <li className="flex gap-1.5">
                  <Check size={12} className="mt-0.5 text-[#4F6F52] shrink-0" strokeWidth={2.4} />
                  Weekly Reflections
                </li>
                <li className="flex gap-1.5">
                  <Check size={12} className="mt-0.5 text-[#4F6F52] shrink-0" strokeWidth={2.4} />
                  Full history &amp; memory
                </li>
              </ul>
            </div>
          </div>

          {/* Two-tier pricing — Monthly + Annual side by side. The annual
              tier saves the visitor about $8 versus 12× monthly ($47.88 -
              $39.99). We render the two Stripe tiers as parallel cards so
              the choice is obvious without hiding either option. */}
          <div
            data-testid="paywall-pricing-tiers"
            className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 mt-1 mb-3"
          >
            {/* Monthly */}
            <button
              type="button"
              onClick={() => startCheckout("pro")}
              disabled={!!busy || !pro}
              data-testid="tier-pro-cta"
              className="text-left rounded-2xl border border-[#DCE5DB] bg-white hover:border-[#4F6F52]/60 hover:bg-[#F9FAF7] disabled:opacity-60 p-4 transition"
            >
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#6B6B6B] font-semibold mb-1">
                Monthly
              </div>
              <div className="font-display text-2xl text-[#2E2E2E] leading-none">
                ${monthlyAmount}
                <span className="text-sm text-[#6B6B6B] font-normal">/month</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#4F6F52] font-semibold">
                {busy === "pro" ? "Opening…" : "Start monthly"}
                {busy !== "pro" && <ArrowRight size={13} strokeWidth={2.4} />}
              </div>
            </button>

            {/* Annual — highlighted */}
            <button
              type="button"
              onClick={() => startCheckout("pro_annual")}
              disabled={!!busy || !proAnnual}
              data-testid="tier-pro-annual-cta"
              className="relative text-left rounded-2xl border-2 border-[#4F6F52] bg-[#EEF3EC] hover:bg-[#E1ECDD] disabled:opacity-60 p-4 transition shadow-[0_4px_14px_rgba(79,111,82,0.15)]"
            >
              <div className="absolute -top-2 right-3 bg-[#4F6F52] text-white text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full">
                Best value
              </div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1">
                Annual
              </div>
              <div className="font-display text-2xl text-[#2E2E2E] leading-none">
                ${annualAmount}
                <span className="text-sm text-[#6B6B6B] font-normal">/year</span>
              </div>
              <div className="text-[11px] text-[#4F6F52] mt-1 font-medium">
                Save about $8 compared with monthly
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#4F6F52] font-semibold">
                {busy === "pro_annual" ? "Opening…" : "Start annual"}
                {busy !== "pro_annual" && <ArrowRight size={13} strokeWidth={2.4} />}
              </div>
            </button>
          </div>

          {/* Secondary CTA — opens the existing gift flow. No new checkout path. */}
          <button
            onClick={() => setGiftOpen(true)}
            data-testid="paywall-gift-cta"
            className="w-full mt-2.5 bg-white hover:bg-[#EEF3EC] text-[#3F5A42] border border-[#B7CCB1] hover:border-[#4F6F52]/60 rounded-full px-5 py-3 font-semibold inline-flex items-center justify-center gap-2 transition"
          >
            <Gift size={14} strokeWidth={2.4} /> Give LifeGuide as a Gift
          </button>
          <p className="text-[11.5px] text-[#4E5A4C] text-center mt-1 leading-relaxed">
            Send 1, 3, 6, or 12 months — anonymously or from you.
          </p>

          <p className="text-[11px] text-[#6B6B6B] text-center mt-2.5 leading-relaxed">
            Secure payment via Stripe · Cancel anytime
            {user?.email && ` · ${user.email}`}
          </p>
        </div>
      </div>
      <GiftModal open={giftOpen} onClose={() => setGiftOpen(false)} />
    </div>
  );
}
